import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import Layout from '../components/Layout';
import './AlbumsPage.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AlbumsPage = () => {
  const navigate = useNavigate();
  const { user, logout, sessionToken } = useAuth();
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);

  useEffect(() => {
    loadAlbums();
  }, []);

  const loadAlbums = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/albums`, {
        headers: {
          Authorization: `Bearer ${sessionToken}`
        }
      });

      if (response.data.success) {
        setAlbums(response.data.albums);
      }
    } catch (error) {
      console.error('Error loading albums:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAlbum = () => {
    setShowCreateModal(true);
  };

  const handleViewAlbum = (album) => {
    setSelectedAlbum(album);
    setShowUploadModal(true);
  };

  const handleDeleteAlbum = async (albumId) => {
    if (!window.confirm('Are you sure you want to delete this album?')) {
      return;
    }

    try {
      const response = await axios.delete(`${API}/albums/${albumId}`, {
        headers: {
          Authorization: `Bearer ${sessionToken}`
        }
      });

      if (response.data.success) {
        await loadAlbums();
      }
    } catch (error) {
      console.error('Error deleting album:', error);
      alert('Failed to delete album');
    }
  };

  return (
    <Layout>
      <div className="albums-page">
        <div className="albums-container">
        <div className="albums-header">
          <h1>My Model(s) Album</h1>
          <p>Create albums with your model photos for personalized virtual try-ons</p>
          <button className="create-album-btn" onClick={handleCreateAlbum}>
            + Create New Album
          </button>
        </div>

        {loading ? (
          <div className="loading">Loading albums...</div>
        ) : albums.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📸</div>
            <h3>No Albums Yet</h3>
            <p>Create your first album to store your photos and start personalizing your try-on experience</p>
            <button className="create-album-btn" onClick={handleCreateAlbum}>
              Create Your First Album
            </button>
          </div>
        ) : (
          <div className="albums-grid">
            {albums.map((album) => (
              <div key={album.id} className="album-card">
                <div className="album-header">
                  <h3>{album.name}</h3>
                  <span className={`album-badge ${album.category}`}>
                    {album.category === 'me' && '👤 Me'}
                    {album.category === 'partner' && '👫 Partner'}
                    {album.category === 'family' && '👨‍👩‍👧‍👦 Family'}
                    {album.category === 'custom' && '📁 Custom'}
                  </span>
                </div>
                
                <div className="album-photos">
                  {album.photo_count === 0 ? (
                    <div className="no-photos">
                      <p>No photos uploaded yet</p>
                    </div>
                  ) : (
                    <div className="photo-grid">
                      {album.photos.map((photo, idx) => (
                        <div key={idx} className="photo-thumbnail">
                          <img 
                            src={photo.image_base64.startsWith('data:') ? photo.image_base64 : `data:image/jpeg;base64,${photo.image_base64}`} 
                            alt={photo.position}
                          />
                          <span className="photo-label">{photo.position}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="album-status">
                  <span className={`status-badge ${album.is_complete ? 'complete' : 'incomplete'}`}>
                    {album.is_complete ? '✓ Complete (3/3)' : `${album.photo_count}/3 photos`}
                  </span>
                </div>

                <div className="album-actions">
                  <button className="btn-primary" onClick={() => handleViewAlbum(album)}>
                    {album.photo_count === 0 ? 'Upload Photos' : 'Manage Photos'}
                  </button>
                  <button className="btn-danger" onClick={() => handleDeleteAlbum(album.id)}>
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        </div>

        {showCreateModal && (
          <CreateAlbumModal
            onClose={() => setShowCreateModal(false)}
            onSuccess={loadAlbums}
            sessionToken={sessionToken}
          />
        )}

        {showUploadModal && selectedAlbum && (
          <UploadPhotosModal
            album={selectedAlbum}
            onClose={() => {
              setShowUploadModal(false);
              setSelectedAlbum(null);
              loadAlbums();
            }}
            sessionToken={sessionToken}
          />
        )}
      </div>
    </Layout>
  );
};

// Create Album Modal Component
const CreateAlbumModal = ({ onClose, onSuccess, sessionToken }) => {
  const [albumName, setAlbumName] = useState('');
  const [category, setCategory] = useState('me');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const predefinedCategories = [
    { value: 'me', label: '👤 For Me', defaultName: 'My Album' },
    { value: 'partner', label: '👫 For Partner', defaultName: 'Partner Album' },
    { value: 'family', label: '👨‍👩‍👧‍👦 For Family Member', defaultName: 'Family Album' },
    { value: 'custom', label: '➕ Custom', defaultName: '' }
  ];

  const handleCategoryChange = (newCategory) => {
    setCategory(newCategory);
    const selected = predefinedCategories.find(c => c.value === newCategory);
    if (selected && newCategory !== 'custom') {
      setAlbumName(selected.defaultName);
    } else {
      setAlbumName('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!albumName.trim()) {
      setError('Please enter an album name');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const response = await axios.post(
        `${API}/albums`,
        {
          name: albumName,
          category: category
        },
        {
          headers: {
            'Authorization': `Bearer ${sessionToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        onSuccess();
        onClose();
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create album');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h2>Create New Album</h2>
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Album Category</label>
            <div className="category-options">
              {predefinedCategories.map((cat) => (
                <button
                  key={cat.value}
                  type="button"
                  className={`category-btn ${category === cat.value ? 'active' : ''}`}
                  onClick={() => handleCategoryChange(cat.value)}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label>Album Name</label>
            <input
              type="text"
              value={albumName}
              onChange={(e) => setAlbumName(e.target.value)}
              placeholder={category === 'custom' ? 'Enter custom album name' : 'Album name'}
              required
            />
          </div>

          {error && <div className="error-message">{error}</div>}

          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Creating...' : 'Create Album'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Upload Photos Modal Component
const UploadPhotosModal = ({ album, onClose, sessionToken }) => {
  const [uploadingPosition, setUploadingPosition] = useState(null);
  const [uploadStatus, setUploadStatus] = useState({});
  const [error, setError] = useState('');

  const positions = ['front', 'side', 'back'];

  const getPhotoForPosition = (position) => {
    return album.photos?.find(p => p.position === position);
  };

  const handleFileSelect = async (position, event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('Image file size too large (max 10MB)');
      return;
    }

    try {
      setUploadingPosition(position);
      setError('');

      // Convert to base64
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64Image = reader.result;

        try {
          const response = await axios.post(
            `${API}/albums/${album.id}/photos`,
            {
              position: position,
              image_base64: base64Image
            },
            {
              headers: {
                'Authorization': `Bearer ${sessionToken}`,
                'Content-Type': 'application/json'
              }
            }
          );

          if (response.data.success) {
            setUploadStatus(prev => ({
              ...prev,
              [position]: { success: true, message: response.data.message }
            }));
            
            // Refresh album data
            setTimeout(() => {
              window.location.reload();
            }, 1000);
          } else {
            setUploadStatus(prev => ({
              ...prev,
              [position]: { success: false, message: response.data.error }
            }));
            setError(response.data.error);
          }
        } catch (err) {
          const errorMsg = err.response?.data?.detail || 'Failed to upload photo';
          setUploadStatus(prev => ({
            ...prev,
            [position]: { success: false, message: errorMsg }
          }));
          setError(errorMsg);
        } finally {
          setUploadingPosition(null);
        }
      };

      reader.readAsDataURL(file);
    } catch (err) {
      setError('Failed to process image');
      setUploadingPosition(null);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content upload-modal" onClick={(e) => e.stopPropagation()}>
        <h2>Upload Photos for {album.name}</h2>
        <p className="modal-subtitle">Upload 3 photos: Front view, Side view, and Back view</p>

        <div className="content-policy-notice">
          <div className="notice-icon">⚠️</div>
          <div className="notice-content">
            <strong>Content Policy:</strong> Please upload appropriate photos only. 
            Images containing nudity, explicit content, violence, or offensive material will be automatically rejected.
          </div>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="upload-grid">
          {positions.map((position) => {
            const existingPhoto = getPhotoForPosition(position);
            const status = uploadStatus[position];

            return (
              <div key={position} className="upload-slot">
                <h4>{position.charAt(0).toUpperCase() + position.slice(1)} View</h4>
                
                <div className={`upload-area ${existingPhoto ? 'has-photo' : ''}`}>
                  {existingPhoto ? (
                    <div className="existing-photo">
                      <img 
                        src={existingPhoto.image_base64.startsWith('data:') ? existingPhoto.image_base64 : `data:image/jpeg;base64,${existingPhoto.image_base64}`}
                        alt={position}
                      />
                      <div className="photo-overlay">
                        <label className="replace-btn">
                          Replace Photo
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleFileSelect(position, e)}
                            disabled={uploadingPosition === position}
                            style={{ display: 'none' }}
                          />
                        </label>
                      </div>
                    </div>
                  ) : (
                    <label className="upload-placeholder">
                      <div className="upload-icon">📸</div>
                      <p>Click to upload</p>
                      <span className="upload-hint">{position} view</span>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleFileSelect(position, e)}
                        disabled={uploadingPosition === position}
                        style={{ display: 'none' }}
                      />
                    </label>
                  )}
                </div>

                {uploadingPosition === position && (
                  <div className="upload-progress">Uploading and validating...</div>
                )}

                {status && (
                  <div className={`upload-status ${status.success ? 'success' : 'error'}`}>
                    {status.success ? '✓' : '✗'} {status.message}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="photo-requirements">
          <h4>📋 Photo Requirements:</h4>
          <ul>
            <li>✓ Clear, well-lit photos</li>
            <li>✓ Full body visible</li>
            <li>✓ Face clearly visible</li>
            <li>✓ Single person only</li>
            <li>✓ Minimum resolution: 800x800 pixels</li>
            <li>✓ Maximum file size: 10MB</li>
          </ul>
        </div>

        <div className="modal-actions">
          <button className="btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default AlbumsPage;
